import React, { useState, useContext, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { PyodideContext } from '../contexts/PyodideContext';
import { rLanguage } from '../monaco/rLanguage';
import { execute } from '../api/client';

const CodeRunner = ({ autoDetect = true, resetTrigger }) => {
  const { pyodide, loading: pyodideLoading, error: pyodideError } = useContext(PyodideContext);
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('auto');
  const [output, setOutput] = useState('');
  const [editorReady, setEditorReady] = useState(false);

  // Reset editor when lesson/tab changes
  useEffect(() => {
    setCode('');
    setOutput('');
    setLanguage('auto');
  }, [resetTrigger]);

  // Auto-detect language
  useEffect(() => {
    if (autoDetect && code !== '') {
      const detected = detectLanguage(code);
      console.log('Detected language:', detected);
      setLanguage(detected);
    }
  }, [code, autoDetect]);

  const detectLanguage = (text) => {
    const lower = text.toLowerCase();

    const isLikelyR =
      /<-\s*|library\(|plot\(|c\(|paste\(|summary\(|mean\(|\bx\s*<-\s*\b|\by\s*<-\s*\b/.test(lower);

    const isLikelyPython =
      /\bdef\b|\bimport\b|print\(|for\s*\(|while\s*\(|if\s*\(|elif\s+|else:/.test(lower);

    const isLikelyHTML =
      /<html>|<\w+>[\s\S]*<\/\w+>/i.test(text);

    if (isLikelyHTML) return 'html';
    if (isLikelyR && !isLikelyPython) return 'r';
    if (isLikelyPython) return 'python';

    return 'python';
  };

  const clearCode = () => {
    setCode('');
    setOutput('🧼 Code cleared.');
  };

  const runCode = async () => {
    const lang = language;

    try {
      if (lang === 'html') {
        setOutput('iframe');
      } else if (lang === 'python') {
        if (pyodideError) {
          setOutput(`❌ Python failed to load: ${pyodideError.message}. Try reloading the page.`);
          return;
        }
        if (pyodideLoading || !pyodide) {
          setOutput('⏳ Python is still loading...');
          return;
        }

        try {
          await pyodide.runPythonAsync(`
import sys
from io import StringIO
sys.stdout = mystdout = StringIO()
          `);
          await pyodide.runPythonAsync(code);
          const pyOutput = await pyodide.runPythonAsync('mystdout.getvalue()');
          setOutput(pyOutput || '✔️ Code ran with no output.');
        } catch (err) {
          setOutput(`❌ Python Error: ${err.message}`);
        }
      } else if (lang === 'r') {
        const res = await execute.r(code);
        const data = res.data;
        if (data.image) {
          setOutput(data.image);
        } else {
          setOutput(data.output || data.error || '✔️ R code ran.');
        }
      }
    } catch (err) {
      setOutput(`❌ ${err.message}`);
    }
  };

  const handleBeforeMount = (monaco) => {
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'r')) {
      monaco.languages.register({ id: 'r' });
      monaco.languages.setMonarchTokensProvider('r', rLanguage);
    }

    monaco.editor.defineTheme('rTheme', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'C586C0' },
        { token: 'type.identifier', foreground: '569CD6' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'comment', foreground: '6A9955' },
      ],
      colors: {
        'editor.foreground': '#FFFFFF',
        'editor.background': '#1E1E1E',
      }
    });
  };

  const handleEditorDidMount = () => {
    setEditorReady(true);
  };

  return (
    <div className="code-runner">
      <div style={{ marginBottom: 8 }}>
        <select onChange={(e) => setLanguage(e.target.value)} value={language}>
          <option value="auto">Auto Detect</option>
          <option value="python">Python</option>
          <option value="html">HTML</option>
          <option value="r">R</option>
        </select>
      </div>

      {(language === 'python' || language === 'auto') && pyodideLoading && (
        <div style={{ marginBottom: 8, color: '#666' }}>
          🐍 Loading Python environment... (first load can take a bit)
        </div>
      )}
      {(language === 'python' || language === 'auto') && pyodideError && (
        <div style={{ marginBottom: 8, color: '#c62828' }}>
          ❌ Python environment failed to load: {pyodideError.message}
        </div>
      )}

      <div style={{ height: '350px', border: '1px solid #ccc', marginBottom: '1rem' }}>
        <Editor
          key={`${language}-${resetTrigger}`} // full remount per tab
          height="100%"
          language={language}
          value={code}
          onChange={(val) => setCode(val || '')}
          theme="rTheme"
          beforeMount={handleBeforeMount}
          onMount={handleEditorDidMount}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <button onClick={runCode} disabled={!editorReady}>▶️ Run</button>
        <button onClick={clearCode} style={{ marginLeft: '1rem', backgroundColor: '#eee' }}>🧼 Clear</button>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <h4>Output:</h4>
        {output === 'iframe' ? (
          <iframe
            title="HTML Preview"
            srcDoc={`<html><head><style>body{background:#fff;color:#000;font-family:sans-serif;padding:1rem;}</style></head><body>${code}</body></html>`}
            sandbox="allow-scripts"
            style={{ width: '100%', height: '300px', border: '1px solid #ccc' }}
          />
        ) : output?.startsWith('data:image') ? (
          <img src={output} alt="R Plot" style={{ maxWidth: '100%', border: '1px solid #ccc' }} />
        ) : (
          <pre className="output">{output || 'ℹ️ No output yet.'}</pre>
        )}
      </div>
    </div>
  );
};

export default CodeRunner;
